import { Icon } from 'idea-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { ChangeEvent, Component, DragEvent, FormEvent } from 'react';
import {
    Button,
    Card,
    Col,
    Container,
    Form,
    ProgressBar,
    Row,
    ToggleButton
} from 'react-bootstrap';
import { sleep } from 'web-utility';

import { MAX_FILE_SIZE } from '../model/Document';
import { t } from '../model/Translation';

interface AIAnalysisOption {
    id: string;
    title: string;
    description: string;
    checked: boolean;
}

const validTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

@observer
export class OptimizationTaskCreatorPage extends Component {
    @observable
    accessor selectedFile: File | undefined;

    @observable
    accessor uploadProgress = 0;

    @observable
    accessor formatValidation: {
        status: 'idle' | 'validating' | 'success' | 'error';
        message?: string;
        rowCount?: number;
        totalRows?: number;
    } = { status: 'idle' };

    @observable
    accessor analysisOptions: AIAnalysisOption[] = [
        {
            id: 'classification',
            title: t('work_order_classification_optimization'),
            description: t('auto_identify_work_orders'),
            checked: true
        },
        {
            id: 'deduplication',
            title: t('duplicate_work_order_extraction'),
            description: t('identify_duplicate_records'),
            checked: true
        },
        {
            id: 'sensitive',
            title: t('sensitive_work_order_extraction'),
            description: t('auto_detect_sensitive_data'),
            checked: false
        },
        {
            id: 'employee',
            title: t('employee_work_order_audit'),
            description: t('analyze_employee_handling'),
            checked: false
        }
    ];

    handleFileSelect = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
        const [file] = currentTarget.files || [];

        if (file) this.handleFile(file);
    };

    handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const [file] = event.dataTransfer.files || [];

        if (file) this.handleFile(file);
    };

    handleDragOver = (event: DragEvent<HTMLDivElement>) =>
        event.preventDefault();

    handleFile = async (file: File) => {
        if (!validTypes.includes(file.type) && !file.name.endsWith('.xls'))
            return (this.formatValidation = {
                status: 'error',
                message: t('file_format_not_correct')
            });

        if (file.size > MAX_FILE_SIZE)
            return (this.formatValidation = {
                status: 'error',
                message: t('file_exceeds_max_size', { size: '10MB' })
            });

        this.selectedFile = file;
        this.formatValidation = { status: 'validating' };

        // 模拟上传进度
        await this.simulateUpload();

        // 模拟格式验证
        await this.simulateValidation();
    };

    simulateUpload = async () => {
        for (let i = 0; i <= 100; i += 10) {
            await sleep();
            this.uploadProgress = i;
        }
    };

    simulateValidation = async () => {
        await sleep();

        // 模拟验证成功
        this.formatValidation = {
            status: 'success',
            rowCount: 4300,
            totalRows: 4320,
            message: undefined
        };
    };

    handleOptionToggle = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
        const { id, checked } = currentTarget;
        const option = this.analysisOptions.find(opt => opt.id === id);

        if (option) option.checked = checked;
    };

    handleReselect = () => {
        this.selectedFile = null;
        this.uploadProgress = 0;
        this.formatValidation = { status: 'idle' };
    };

    handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        // TODO: 实现提交逻辑
        console.log('Submitting task with options:', this.analysisOptions);
    };

    renderUploadArea = () => (
        <Card
            className="text-center"
            style={{ cursor: 'pointer' }}
            body
            onDrop={this.handleDrop}
            onDragOver={this.handleDragOver}
        >
            <Icon
                name="cloud-upload"
                style={{ fontSize: '64px', color: '#6c757d' }}
            />
            <h4 className="mt-3">{t('upload_work_order_file')}</h4>
            <p className="text-muted">
                {t('support_excel_format')}, {t('max_size_10mb')}
            </p>
            <label
                className="btn btn-primary mt-2"
                style={{ cursor: 'pointer' }}
            >
                {t('select_file')}
                <input
                    type="file"
                    className="d-none"
                    accept=".xls,.xlsx"
                    onChange={this.handleFileSelect}
                />
            </label>
            <p className="text-muted small mt-3">{t('or_drag_file_here')}</p>
        </Card>
    );

    renderFileStatus() {
        const { uploadProgress, formatValidation } = this;

        return (
            <>
                <Card body>
                    <h5>{t('uploading')}</h5>

                    <ProgressBar
                        now={uploadProgress}
                        label={`${uploadProgress}%`}
                        className="mb-3"
                        style={{ height: '24px' }}
                        variant={uploadProgress === 100 ? 'success' : 'primary'}
                    />
                </Card>

                <Card body>
                    <h5>{t('format_validation')}</h5>

                    {formatValidation.status === 'error' && (
                        <div className="d-flex align-items-start text-danger mt-3">
                            <Icon name="x-circle" className="me-2 mt-1" />
                            <div>
                                <div className="fw-bold">
                                    {t('file_format_not_correct')}
                                </div>
                                <div className="small">
                                    {t('error_label')}: [{t('column_missing')}]
                                    [{t('wrong_file_type')}]{' '}
                                    {t('word_vs_expected_format')}
                                </div>
                                <div className="text-muted small mt-1">
                                    {t('check_file_not_correct')}
                                </div>
                            </div>
                        </div>
                    )}
                    {formatValidation.status === 'success' && (
                        <div className="d-flex align-items-start text-success mt-3">
                            <Icon name="check-circle" className="me-2 mt-1" />
                            <div>
                                <div className="fw-bold">
                                    {t('work_order_count')}: (
                                    {formatValidation.rowCount} /{' '}
                                    {formatValidation.totalRows})
                                </div>
                                <div className="text-muted small">
                                    {t('uploaded_orders')}:{' '}
                                    {formatValidation.rowCount},{' '}
                                    {t('orders_excluded')}
                                </div>
                            </div>
                        </div>
                    )}
                    <footer className="text-center">
                        <Button
                            variant="primary"
                            className="mt-3"
                            onClick={this.handleReselect}
                        >
                            {t('reselect_file')}
                        </Button>
                    </footer>
                </Card>
            </>
        );
    }

    renderAnalysisOptions() {
        const { formatValidation } = this;
        const isDisabled = formatValidation.status !== 'success';

        return (
            <Card body>
                <h5>{t('ai_analysis_capabilities')}</h5>

                <Row as="ul" className="list-unstyled g-3 mt-1" xs={2}>
                    {this.analysisOptions.map(option => (
                        <Col key={option.id} as="li">
                            <ToggleButton
                                className="w-100 h-100 text-start"
                                variant="outline-primary"
                                type="checkbox"
                                name="type"
                                value={option.id}
                                id={option.id}
                                checked={option.checked}
                                onChange={this.handleOptionToggle}
                            >
                                <div className="d-flex align-items-center mb-2">
                                    <h6 className="mb-0">{option.title}</h6>
                                    {option.checked && !isDisabled && (
                                        <Icon
                                            name="check-circle-fill"
                                            className="ms-auto text-primary"
                                        />
                                    )}
                                </div>
                                <p className="text-muted small mb-0">
                                    {option.description}
                                </p>
                            </ToggleButton>
                        </Col>
                    ))}
                </Row>

                <div className="text-center mt-3">
                    <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={isDisabled}
                        style={{ minWidth: '200px' }}
                    >
                        <Icon name="play-fill" /> {t('start_now')}
                    </Button>
                </div>
            </Card>
        );
    }

    render() {
        const { selectedFile } = this;

        return (
            <Container className="py-4">
                <Form
                    className="d-flex flex-column gap-3"
                    onSubmit={this.handleSubmit}
                >
                    {this.renderUploadArea()}
                    {selectedFile && this.renderFileStatus()}
                    {this.renderAnalysisOptions()}
                </Form>
            </Container>
        );
    }
}
